// Form creazione nuova azienda cliente con lookup VIES per P.IVA
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Search, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import api from '../../lib/api'

interface VatLookupResult {
  valid: boolean
  vatNumber: string
  countryCode: string
  name?: string
  address?: string
  city?: string
  postalCode?: string
  province?: string
  error?: string
}

const companySchema = z.object({
  name: z.string().min(1, 'Ragione sociale obbligatoria'),
  vatNumber: z.string().optional(),
  fiscalCode: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email('Email non valida').optional().or(z.literal('')),
  pecEmail: z.string().email('PEC non valida').optional().or(z.literal('')),
})

type CompanyFormData = z.infer<typeof companySchema>

interface CreatedCompany {
  id: string
  name: string
}

/**
 * Pagina per la creazione di una nuova azienda cliente.
 * Naviga a /companies/:id dopo la creazione.
 */
export function NewCompanyPage() {
  const navigate = useNavigate()
  const [lookupStatus, setLookupStatus] = useState<
    { type: 'idle' } | { type: 'loading' } | { type: 'success'; data: VatLookupResult } | { type: 'error'; message: string }
  >({ type: 'idle' })

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      vatNumber: '',
      fiscalCode: '',
      address: '',
      city: '',
      postalCode: '',
      phone: '',
      email: '',
      pecEmail: '',
    },
  })

  // Lookup P.IVA tramite VIES (API pubblica UE)
  async function handleVatLookup() {
    const vat = getValues('vatNumber')?.trim()
    if (!vat) {
      setLookupStatus({ type: 'error', message: 'Inserisci prima la P.IVA.' })
      return
    }
    setLookupStatus({ type: 'loading' })
    try {
      const res = await api.get<VatLookupResult>(`/companies/vat-lookup/${encodeURIComponent(vat)}`)
      const result = res.data
      if (!result.valid) {
        setLookupStatus({ type: 'error', message: result.error ?? 'P.IVA non valida o non attiva.' })
        return
      }
      // Popola automaticamente i campi del form con i dati VIES
      if (result.name) setValue('name', result.name, { shouldValidate: true })
      if (result.address) setValue('address', result.address)
      if (result.city) setValue('city', result.city)
      if (result.postalCode) setValue('postalCode', result.postalCode)
      setLookupStatus({ type: 'success', data: result })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore di connessione VIES'
      setLookupStatus({ type: 'error', message })
    }
  }

  const createMutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      // Rimuove i campi stringa vuoti per non inviare stringhe vuote al backend
      const cleaned = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== '' && v !== undefined)
      )
      const res = await api.post<CreatedCompany>('/companies', cleaned)
      return res.data
    },
    onSuccess: (company) => {
      navigate(`/companies/${company.id}`)
    },
  })

  return (
    <div className="space-y-4 max-w-2xl">
      <PageHeader
        title="Nuovo cliente"
        breadcrumb={[
          { label: 'Clienti', href: '/companies' },
          { label: 'Nuovo cliente' },
        ]}
      />

      <Card>
        <form
          onSubmit={handleSubmit((data) => createMutation.mutate(data))}
          className="space-y-4"
        >
          {/* Dati principali */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Dati aziendali</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Ragione sociale *"
                {...register('name')}
                error={errors.name?.message}
                placeholder="Acme S.r.l."
                className="sm:col-span-2"
              />
              <div>
                <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                  Partita IVA
                </label>
                <div className="flex gap-2">
                  <Input
                    {...register('vatNumber')}
                    placeholder="12345678901"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={handleVatLookup}
                    disabled={lookupStatus.type === 'loading'}
                    title="Verifica P.IVA e importa dati da VIES"
                  >
                    {lookupStatus.type === 'loading' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span className="ml-1 hidden sm:inline">Verifica</span>
                  </Button>
                </div>
                {lookupStatus.type === 'success' && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-[var(--fc-success)]">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>P.IVA attiva — dati importati da VIES</span>
                  </div>
                )}
                {lookupStatus.type === 'error' && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-[var(--fc-danger)]">
                    <XCircle className="w-3 h-3" />
                    <span>{lookupStatus.message}</span>
                  </div>
                )}
              </div>
              <Input
                label="Codice Fiscale"
                {...register('fiscalCode')}
                placeholder="RSSMRA80A01H501Z"
              />
            </div>
          </div>

          {/* Indirizzo */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Indirizzo sede legale</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input
                label="Indirizzo"
                {...register('address')}
                placeholder="Via Roma 1"
                className="sm:col-span-2"
              />
              <Input
                label="CAP"
                {...register('postalCode')}
                placeholder="20121"
              />
              <Input
                label="Città"
                {...register('city')}
                placeholder="Milano"
                className="sm:col-span-3"
              />
            </div>
          </div>

          {/* Contatti */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Contatti</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Telefono"
                {...register('phone')}
                placeholder="+39 02 1234567"
              />
              <Input
                label="Email"
                type="email"
                {...register('email')}
                error={errors.email?.message}
                placeholder="info@azienda.it"
              />
              <Input
                label="PEC"
                type="email"
                {...register('pecEmail')}
                error={errors.pecEmail?.message}
                placeholder="azienda@pec.it"
              />
            </div>
          </div>

          {/* Errore globale */}
          {createMutation.isError && (
            <Alert variant="error">
              Errore durante la creazione. Verificare i dati e riprovare.
            </Alert>
          )}

          {/* Azioni */}
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--fc-border)]">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/companies')}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creazione...' : 'Crea cliente'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
